import { STANDARD_REGISTRATION_FIELDS } from '../constants/registration-standard-fields';
import { US_STATES } from '../constants/states';
import type { CreateFormRequestDto } from '../models/dto/forms.dto';
import {
	DEFAULT_FORM_SETTINGS,
	type FormFieldDefinitionModel,
	type FormPurpose,
	type FormRuleModel,
	type FormSettingsModel,
} from './form-types';

/**
 * Stock forms (owner decision F12; docs/claude/forms/01-design.md §7): Contact,
 * Registration and Blank are starting points Workd ships. "New form" copies one into the
 * brand, and from then on the copy is the brand's own: every field and rule is editable,
 * and a later change here never reaches a form already created from it.
 */

export const FORM_TEMPLATE_KEYS = ['contact', 'registration', 'blank'] as const;

export type FormTemplateKey = (typeof FORM_TEMPLATE_KEYS)[number];

export interface FormTemplateModel {
	label: string;
	description: string;
	purpose: FormPurpose;
	defaultName: string;
	defaultFormKey: string;
	fields: Array<FormFieldDefinitionModel>;
	rules: Array<FormRuleModel>;
}

type TemplateFieldModel = Partial<FormFieldDefinitionModel> &
	Pick<FormFieldDefinitionModel, 'fieldKey' | 'label' | 'fieldType'>;

function field(input: TemplateFieldModel): FormFieldDefinitionModel {
	return {
		adminLabel: null,
		placeholder: null,
		helpText: null,
		isRequired: false,
		sortOrder: 0,
		defaultValue: null,
		selectOptions: null,
		validationRules: null,
		identity: null,
		consentPurpose: null,
		policyVersions: null,
		includeInNotification: true,
		...input,
	};
}

/**
 * The two confirmations every form that collects personal information carries (§2, the
 * SOC 2 control workd.com applies to its own forms). `policyVersions` stays null: the
 * server fills a `terms` confirmation from the brand's Web Settings when a page reads or
 * submits the form, so a policy update is recorded without editing every form.
 * Left out of the notification email: the submission already records them.
 */
function consentFields(): Array<FormFieldDefinitionModel> {
	return [
		field({
			fieldKey: 'confirm_accurate',
			label: 'I confirm my contact details are accurate',
			adminLabel: 'Details confirmed accurate',
			fieldType: 'consent',
			isRequired: true,
			consentPurpose: 'accuracy',
			includeInNotification: false,
		}),
		field({
			fieldKey: 'accept_terms',
			label: 'I accept the Privacy Policy and Terms of Service',
			adminLabel: 'Accepted Privacy Policy and Terms',
			fieldType: 'consent',
			isRequired: true,
			consentPurpose: 'terms',
			includeInNotification: false,
		}),
	];
}

/** Display order is the array order; sortOrder is written to match it. */
function inOrder(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
): Array<FormFieldDefinitionModel> {
	return fields.map((definition, index) => ({
		...definition,
		sortOrder: index,
	}));
}

/**
 * How the storefront draws each standard registration field today (RegistrationForm.tsx),
 * so a brand's registration form starts out asking the same questions the same way. A
 * field not listed here is plain text.
 */
const STANDARD_FIELD_SHAPES: Readonly<
	Record<string, Omit<TemplateFieldModel, 'fieldKey' | 'label'>>
> = {
	// The company name is what the lead is created under.
	companyName: { fieldType: 'text', identity: 'company' },
	// Today's page offers the US states and territories in a drop-down.
	state: {
		fieldType: 'select',
		selectOptions: US_STATES.map((usState) => ({
			value: usState.value,
			label: usState.label,
		})),
	},
	businessType: {
		fieldType: 'text',
		placeholder: 'Restaurant, grocery, distributor, …',
	},
	leadSource: {
		fieldType: 'text',
		placeholder: 'Trade show, referral, search, …',
	},
	notes: { fieldType: 'textarea' },
};

function contactTemplate(): FormTemplateModel {
	return {
		label: 'Contact',
		description:
			'Name, email or phone, company and a message. For a "Contact us" page.',
		purpose: 'contact',
		defaultName: 'Contact',
		defaultFormKey: 'contact',
		fields: inOrder([
			field({
				fieldKey: 'firstName',
				label: 'First Name',
				fieldType: 'text',
				identity: 'first_name',
				isRequired: true,
			}),
			field({
				fieldKey: 'lastName',
				label: 'Last Name',
				fieldType: 'text',
				identity: 'last_name',
				isRequired: true,
			}),
			field({
				fieldKey: 'email',
				label: 'Email',
				fieldType: 'email',
				identity: 'email',
			}),
			field({
				fieldKey: 'phone',
				label: 'Phone',
				fieldType: 'phone',
				identity: 'phone',
			}),
			field({
				fieldKey: 'company',
				label: 'Company',
				fieldType: 'text',
				identity: 'company',
			}),
			field({
				fieldKey: 'message',
				label: 'Message',
				fieldType: 'textarea',
				identity: 'message',
				isRequired: true,
			}),
			...consentFields(),
		]),
		// Either way of replying will do, but one of them is needed to reply at all.
		rules: [{ rule: 'at_least_one', fieldKeys: ['email', 'phone'] }],
	};
}

/**
 * Today's storefront registration form, with its field keys unchanged
 * (02-registration-migration.md, Target) so answers already stored under them keep their
 * meaning: the four contact fields, then each standard field at its default.
 */
function registrationTemplate(): FormTemplateModel {
	return {
		label: 'Registration',
		description:
			'The storefront customer registration: contact, company and address. The Register page uses it.',
		purpose: 'registration',
		defaultName: 'Registration',
		defaultFormKey: 'registration',
		fields: inOrder([
			field({
				fieldKey: 'contactFirstName',
				label: 'First Name',
				fieldType: 'text',
				identity: 'first_name',
				isRequired: true,
			}),
			field({
				fieldKey: 'contactLastName',
				label: 'Last Name',
				fieldType: 'text',
				identity: 'last_name',
				isRequired: true,
			}),
			field({
				fieldKey: 'email',
				label: 'Email',
				fieldType: 'email',
				identity: 'email',
				isRequired: true,
			}),
			field({
				fieldKey: 'phone',
				label: 'Phone',
				fieldType: 'phone',
				identity: 'phone',
				isRequired: true,
			}),
			...STANDARD_REGISTRATION_FIELDS.map((standard) =>
				field({
					fieldType: 'text',
					...STANDARD_FIELD_SHAPES[standard.fieldKey],
					fieldKey: standard.fieldKey,
					label: standard.label,
					isRequired: standard.defaultRequired,
				}),
			),
			...consentFields(),
		]),
		rules: [],
	};
}

function blankTemplate(): FormTemplateModel {
	return {
		label: 'Blank',
		description: 'Only the two required confirmations; add your own questions.',
		purpose: 'custom',
		defaultName: 'New form',
		defaultFormKey: 'form',
		fields: inOrder(consentFields()),
		rules: [],
	};
}

export const FORM_TEMPLATES: Readonly<
	Record<FormTemplateKey, FormTemplateModel>
> = {
	contact: contactTemplate(),
	registration: registrationTemplate(),
	blank: blankTemplate(),
};

/**
 * The create request for a new form copied from a stock form. Everything is copied, so
 * editing the new form never changes the template and a later template change never
 * reaches the form.
 */
export function buildFormFromTemplate(
	key: FormTemplateKey,
	target: {
		name: string;
		formKey: string;
		fkPortalSiteId: number | null;
	},
): CreateFormRequestDto {
	const template = FORM_TEMPLATES[key];
	const settings: FormSettingsModel = {
		...DEFAULT_FORM_SETTINGS,
		blocklist: {
			...DEFAULT_FORM_SETTINGS.blocklist,
			emailDomains: [...DEFAULT_FORM_SETTINGS.blocklist.emailDomains],
			emails: [...DEFAULT_FORM_SETTINGS.blocklist.emails],
			ips: [...DEFAULT_FORM_SETTINGS.blocklist.ips],
			keywords: [...DEFAULT_FORM_SETTINGS.blocklist.keywords],
		},
	};

	return {
		name: target.name,
		formKey: target.formKey,
		fkPortalSiteId: target.fkPortalSiteId,
		purpose: template.purpose,
		settings,
		fields: template.fields.map((definition) => ({
			...definition,
			selectOptions:
				definition.selectOptions?.map((option) => ({ ...option })) ?? null,
			validationRules:
				definition.validationRules != null
					? { ...definition.validationRules }
					: null,
		})),
		fieldRules: template.rules.map((rule) => ({
			...rule,
			fieldKeys: [...rule.fieldKeys],
		})),
	};
}
